const STORAGE_KEY = 'one2one.client_id';

/**
 * Ідентифікатор клієнта для сигналінгу.
 *
 * Живе в `sessionStorage`, а не в `localStorage` — і це свідоме відхилення від
 * мобільного клієнта, який тримає його в спільному для всього застосунку сховищі.
 * У браузері спільний id означав би, що друга вкладка з тією самою кімнатою
 * змусить сервер закрити сокет першої (закриття 1000 «Reconnected»).
 *
 * `sessionStorage` дає рівно потрібну властивість: id стабільний для вкладки —
 * переживає перезавантаження і всі спроби відновлення, — але унікальний між
 * вкладками. Стабільність критична: сервер розпізнає повернення саме за
 * незмінним `client_id`, інакше співрозмовник отримає зайвий `participant_left`.
 */
function generate(): string {
  const timestamp = Date.now();
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${String(timestamp)}-${hex.slice(0, 6)}-${hex.slice(6, 12)}`;
}

/**
 * Повертає ідентифікатор цієї вкладки, створюючи його за першим запитом.
 *
 * Приватний режим і вимкнене сховище не мають ламати дзвінок: якщо сховище
 * недоступне, id лишається в памʼяті модуля — він переживе реконект, але не
 * перезавантаження сторінки.
 */
let inMemory: string | null = null;

export function getClientId(): string {
  if (inMemory !== null) return inMemory;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null && stored.length > 0) {
      inMemory = stored;
      return stored;
    }
    const created = generate();
    sessionStorage.setItem(STORAGE_KEY, created);
    inMemory = created;
    return created;
  } catch {
    const created = generate();
    inMemory = created;
    return created;
  }
}

/** Лише для тестів: скидає памʼять модуля між випадками. */
export function resetClientIdCacheForTests(): void {
  inMemory = null;
}
